'use client';

import * as Styled from './OrganizationsListPage.styled';
import { OrganizationSelection } from '../OrganizationsList/OrganizationsList';
import OrganizationsListNav from '../OrganizationsListNav/OrganizationsListNav';
import { useCommonAuthProvider } from '@modules/auth/providers/CommonAuthProvider/CommonAuthProvider';
import { CustomSpin } from '@modules/shared/components/CustomSpin';
import { isProd } from '@utils/constants';
import { NavActions } from 'app/(authenticated)/_components/NavActions/NavActions';
import CriblIcon from 'app/_assets/icons/CriblIcon.svg';
import { AccessCheckLoader } from 'app/_components/AccessCheckLoader';
import { useOrganization } from 'app/_providers/Organization';
import { useUser } from 'app/_providers/User';

import { Divider } from '@capra/core';
import { Route } from 'next';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import React, { useCallback, useEffect, useState } from 'react';
import { SystemRole } from 'shared/src/authz/rolesV2';
import { MemberAccessCheckType } from 'shared/src/generated/openapi/v3/types';

export const OrganizationsListPage: React.FC = () => {
  const { name, isOEM, memberships: { isLoading: isMembershipsLoading, data: membershipData } } = useUser();
  const auth = useCommonAuthProvider();
  const { organizationId } = useOrganization();
  const router = useRouter();
  const [initials, setInitials] = useState('');
  const searchParams = useSearchParams();
  const selectedTab = searchParams?.get('type');
  const shouldShowOrganizationSelector = selectedTab === 'details' || selectedTab === '' || selectedTab === null;

  // This block is to prevent OEM users coming in organizations page
  const handleOEMRedirect = useCallback(async (): Promise<void> => {
    if (isMembershipsLoading) {
      return;
    }

    if (membershipData && membershipData.length > 0) {
      const selectedMembership = membershipData.find(
        (membership) => auth.oemConnection && (membership.connections)?.includes(auth.oemConnection),
      ) ?? membershipData[0];
      router.push(`/organizations/${selectedMembership.tenant}` as Route);
    }
  }, [membershipData, membershipData, router]);

  useEffect(() => {
    if (name) {
      const fullName = name?.split(' ');
      setInitials(`${fullName[0]?.[0] || ''}${fullName[1]?.[0] || ''}`);
    }

    if (isOEM) {
      handleOEMRedirect();
    }
  }, [handleOEMRedirect, isOEM, router, name, isMembershipsLoading]);

  if (isOEM) {
    return <CustomSpin />;
  }

  return (
    <>
      <Styled.Header>
        <Styled.ContextWrapper>
          <Link
            href={{ pathname: `/organizations/${organizationId}` }}
            passHref
          >
            <CriblIcon />
          </Link>

          <Divider type='vertical' />
        </Styled.ContextWrapper>

        {!isProd && <Styled.NonProductionTag>Non-Production Environment</Styled.NonProductionTag>}

        <NavActions
          userInitials={initials}
          hideProfile
        />
      </Styled.Header>

      <Styled.ListingOrganizationPage>
        <AccessCheckLoader
          accessChecks={[{
            accessType: MemberAccessCheckType.System,
            checks: [SystemRole.ADMIN_READER, SystemRole.SUPPORT],
          }]}
          fallback={<div />}
        >
          <Styled.SideBar>
            <OrganizationsListNav />
          </Styled.SideBar>
        </AccessCheckLoader>

        {shouldShowOrganizationSelector && <OrganizationSelection />}
      </Styled.ListingOrganizationPage>
    </>
  );
};
